import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../security/AuthContext';
import { CorTask } from '../../../types';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { on } from './eventBus';
import { sqlString } from '../../../utils/sql';

/**
 * Hook to get tasks assigned to the current user
 * - Fetches only non-completed tasks from the server
 * - Handles navigation based on task's navigate_to field
 */
export function useAssignedTasks(options?: { statuses?: Array<CorTask['status']> }) {
  const { personnelId, lastLogin } = useAuth();
  const [tasks, setTasks] = useState<CorTask[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const statusesKey = useMemo(() => {
    const s = options?.statuses;
    const list = Array.isArray(s) && s.length
      ? s.map(String)
      : (['pending', 'in progress'] as Array<CorTask['status']>).map(String);
    return list.join('|');
  }, [options?.statuses?.join('|')]);

  const statuses = useMemo(() => {
    const s = options?.statuses;
    if (Array.isArray(s) && s.length) return s;
    return ['pending', 'in progress'] as Array<CorTask['status']>;
  }, [statusesKey]);

  const currentPersonnelId = useMemo(() => {
    return localStorage.getItem('personnel_id') || personnelId;
  }, [personnelId, lastLogin]);

  const fetchTasks = useCallback(async () => {
    if (!currentPersonnelId) { setTasks([]); return; }
    setIsFetching(true);
    setError(null);
    try {
      const pid = String(currentPersonnelId);
      const statusSql = statuses.map((st) => sqlString(String(st))).join(',');
      const query = `SELECT id, description, assigned_to_id, last_action, navigate_to, status, context, created_at
                     FROM cor_tasks
                     WHERE ${sqlString(pid)} = ANY(assigned_to_id)
                       AND status IN (${statusSql})
                     ORDER BY id DESC`;
      const rows = await fetchSqlDataWithQuery(query);
      setTasks(Array.isArray(rows) ? rows as CorTask[] : []);
    } catch (e: any) {
      setError(e);
      setTasks([]);
    } finally {
      setIsFetching(false);
    }
  }, [currentPersonnelId, statusesKey]);

  // Initial fetch and when auth context changes
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, lastLogin]);

  // Refetch on global event via event bus
  useEffect(() => {
    return on('assignedTasks:changed', () => fetchTasks());
  }, [fetchTasks]);

  const navigateToTask = (navigate: (path: string, options?: any) => void, task: CorTask) => {
    const path = task?.navigate_to || '/tasks';
    const purpose = (task as any)?.context?.purpose;
    navigate(path, {
      state: {
        fromTask: true,
        taskId: task?.id,
        ...(purpose ? { purpose } : {}),
      },
    });
  };

  return { tasks, count: tasks.length, isLoading: isFetching, error, navigateToTask };
}

// Notify all listeners (e.g., AppHeader) to refetch assigned tasks
// Deprecated: use emitAssignedTasksChanged from eventBus instead
